/**
 * Data fetching hook for Black Friday page
 *
 * Fetches CMS blocks and categories/products via React Query.
 * Transforms raw GraphQL products to UiBlocks Product interface
 * using mapGraphQLProductToProduct from Theme.
 * Caches categories in localStorage for faster initial render.
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import useLocalStorageState from 'use-local-storage-state'

import { request } from '../../../../Infrastructure'
import { mapGraphQLProductToProduct } from '../../../Theme'
import { getCmsBlock, getCategoryAndProducts } from '../../queries'

import type { Product } from '../../../UiBlocks'
import type {
  BlackFridayCategory,
  BlackFridaySubCategory,
  LocalStorageCategories,
} from '../../types/domain'
import type { CmsBlockItem,
  CmsBlockQueryResponse,
  CategoryQueryResponse,
  GraphQLCategory,
  GraphQLPromotionProduct } from '../../types/graphql'

// Constants
const CATEGORY_ID = '417'
const PAGE_SIZE = '50'
const CURRENT_PAGE = '1'
const BLOCK_IDENTIFIER_1 = 'blackFriday_banner1'
const BLOCK_IDENTIFIER_2 = 'blackFriday_banner2'
const STORAGE_KEY = 'blackFridayCategoriesData'
const STALE_TIME = 15 * 60 * 1000

/** Transform a raw GraphQL product to UiBlocks Product interface */
function transformProduct(product: GraphQLPromotionProduct): Product {
  return mapGraphQLProductToProduct(product)
}

/** Transform a GraphQL category tree to typed domain categories */
function transformCategories(rawCategories: GraphQLCategory[]): BlackFridayCategory[] {
  return rawCategories.map((cat) => ({
    id: cat.id,
    name: cat.name,
    children: cat.children.map((sub): BlackFridaySubCategory => ({
      id: sub.id,
      name: sub.name,
      products: sub.products.items.map(transformProduct),
    })),
  }))
}

export interface BlackFridayDataResult {
  categories: BlackFridayCategory[]
  categoryNames: string[]
  blockData1: CmsBlockItem | null
  blockData2: CmsBlockItem | null
  loading: boolean
  error: Error | null
}

export function useBlackFridayData(): BlackFridayDataResult {
  const [localFridayData, setLocalFridayData] =
    useLocalStorageState<LocalStorageCategories | undefined>(STORAGE_KEY)

  // Fetch CMS blocks
  const cmsBlockQuery = useQuery({
    queryKey: ['blackFridayCmsBlocks', BLOCK_IDENTIFIER_1, BLOCK_IDENTIFIER_2],
    queryFn: () =>
      request<CmsBlockQueryResponse>(getCmsBlock, {
        identifier1: BLOCK_IDENTIFIER_1,
        identifier2: BLOCK_IDENTIFIER_2,
      }),
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  })

  // Fetch categories data
  const categoriesDataQuery = useQuery({
    queryKey: ['blackFridayCategories', CATEGORY_ID, PAGE_SIZE, CURRENT_PAGE],
    queryFn: () =>
      request<CategoryQueryResponse>(getCategoryAndProducts, {
        categoryId: CATEGORY_ID,
        pageSize: PAGE_SIZE,
        currentPage: CURRENT_PAGE,
      }),
    initialData: localFridayData
      ? { data: { categoryList: localFridayData } }
      : undefined,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
  })

  // Derive CMS block data
  const blockData1 = useMemo<CmsBlockItem | null>(
    () => cmsBlockQuery.data?.data?.block1?.items[0] ?? null,
    [cmsBlockQuery.data]
  )

  const blockData2 = useMemo<CmsBlockItem | null>(
    () => cmsBlockQuery.data?.data?.block2?.items[0] ?? null,
    [cmsBlockQuery.data]
  )

  // Derive raw categories from query
  const rawCategories = useMemo<GraphQLCategory[]>(
    () => categoriesDataQuery.data?.data?.categoryList ?? [],
    [categoriesDataQuery.data]
  )

  // Transform to domain types (with UiBlocks-compatible products)
  const categories = useMemo<BlackFridayCategory[]>(
    () => transformCategories(rawCategories),
    [rawCategories]
  )

  // Category names for button list
  const categoryNames = useMemo<string[]>(
    () => categories.map((cat) => cat.name),
    [categories]
  )

  // Persist raw categories to localStorage
  useEffect(() => {
    if (rawCategories.length > 0) {
      setLocalFridayData(rawCategories)
    }
  }, [rawCategories, setLocalFridayData])

  const loading = cmsBlockQuery.isLoading || categoriesDataQuery.isLoading
  const error = (cmsBlockQuery.error ?? categoriesDataQuery.error)

  return { categories, categoryNames, blockData1, blockData2, loading, error }
}
